import Modal, { ModalBody, ModalHeader } from '@ingka/modal';
import { StyledSheets } from '../../../../shared/styles/components/integrations.styles';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { typography } from '../../../../shared/styles/core/utils';

export interface ChangelogEntry {
    ticket: string | null;
    scope: string | null;
    subject: string | null;
}

export interface ChangelogSection {
    type: string;
    entries: ChangelogEntry[];
}

export interface ChangelogVersion {
    version: string;
    date: string;
    sections: ChangelogSection[];
}

interface ChangelogModalProps {
    versions: ChangelogVersion[];
    onClose: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
    feat: { label: 'Features', color: theme.colors.state.success },
    fix: { label: 'Bug Fixes', color: theme.colors.state.warning },
    perf: { label: 'Performance', color: theme.colors.state.info },
};

const defaultTypeConfig = { label: 'Changes', color: theme.colors.state.info };

const formatDate = (iso: string) =>
    new Intl.DateTimeFormat('en-SE', { year: 'numeric', month: 'long', day: 'numeric' }).format(
        new Date(iso)
    );

export const ChangelogModal = ({ versions, onClose }: ChangelogModalProps) => (
    <Modal visible={true} handleCloseBtn={onClose}>
        <StyledSheets
            alignment="right"
            preserveAlignment={true}
            size="small"
            header={<ModalHeader title="What's new" floating={false} />}
            footer={null}
        >
            <ModalBody>
                {versions.length === 0 ? (
                    <EmptyState>No entries yet.</EmptyState>
                ) : (
                    <EntryList>
                        {versions.map((v) => (
                            <VersionGroup key={v.version}>
                                <VersionHeading>
                                    <span>v{v.version}</span>
                                    <VersionDate>{formatDate(v.date)}</VersionDate>
                                </VersionHeading>
                                {v.sections.map((section) => {
                                    const cfg = TYPE_CONFIG[section.type] ?? defaultTypeConfig;
                                    return (
                                        <TypeGroup key={section.type}>
                                            <TypeTag color={cfg.color}>{cfg.label}</TypeTag>
                                            <TypeGroupEntries>
                                                {section.entries.map((entry, i) => (
                                                    <Entry key={i}>
                                                        {(entry.ticket || entry.scope) && (
                                                            <EntryMeta>
                                                                {entry.ticket && <TicketTag>{entry.ticket}</TicketTag>}
                                                                {entry.scope && <Scope>{entry.scope}</Scope>}
                                                            </EntryMeta>
                                                        )}
                                                        {entry.subject && <Subject>{entry.subject}</Subject>}
                                                    </Entry>
                                                ))}
                                            </TypeGroupEntries>
                                        </TypeGroup>
                                    );
                                })}
                            </VersionGroup>
                        ))}
                    </EntryList>
                )}
            </ModalBody>
        </StyledSheets>
    </Modal>
);

const EmptyState = styled.p`
   ${typography.bodyL};
   color: ${theme.colors.text.secondary};
   padding: ${tokens.space.md} 0;
`;

const EntryList = styled.ul`
   list-style: none;
   padding: 0;
   margin: 0;
`;

const VersionGroup = styled.li`
   list-style: none;
   margin-bottom: ${tokens.space.xl};
   &:last-child {
      margin-bottom: 0;
   }
`;

const VersionHeading = styled.h4`
   ${typography.headingS};
   display: flex;
   align-items: baseline;
   gap: ${tokens.space.sm};
   margin: 0 0 ${tokens.space.sm} 0;
   color: ${theme.colors.text.primary};
   border-bottom: ${tokens.border.thin} ${theme.colors.border.subtle};
   padding-bottom: ${tokens.space.xs};
`;

const VersionDate = styled.span`
   ${typography.bodyL};
   color: ${theme.colors.text.tertiary};
   font-weight: ${tokens.font.weight.normal};
   text-transform: none;
   letter-spacing: 0;
`;

const TypeGroup = styled.div`
   margin-bottom: ${tokens.space.md};
   &:last-child {
      margin-bottom: 0;
   }
`;

const TypeGroupEntries = styled.ul`
   list-style: none;
   padding-left: ${tokens.space.sm};
   margin: 0;
`;

const Entry = styled.li`
   padding: ${tokens.space.sm} 0;
   border-bottom: ${tokens.border.thin} ${theme.colors.border.subtle};
   &:last-child {
      border-bottom: none;
   }
`;

const EntryMeta = styled.div`
   display: flex;
   align-items: center;
   gap: ${tokens.space.sm};
   margin-bottom: ${tokens.space.xs};
`;

const TypeTag = styled.span<{ color: string }>`
   ${typography.headingXS};
   padding: 1px ${tokens.space.sm};
   border-radius: ${tokens.radius.sm};
   background: ${({ color }) => color};
   color: ${tokens.neutral[1]};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const TicketTag = styled.span`
   ${typography.bodyL};
   font-weight: ${tokens.font.weight.bold};
   color: ${theme.colors.text.secondary};
   background: ${theme.colors.bg.secondary};
   padding: 0 ${tokens.space.xs};
   border-radius: ${tokens.radius.sm};
   border: ${tokens.border.thin} ${theme.colors.border.default};
`;

const Scope = styled.span`
   ${typography.bodyL};
   color: ${theme.colors.text.tertiary};
`;

const Subject = styled.p`
   ${typography.headingM};
   font-weight: ${tokens.font.weight.normal};
   margin: 0;
   color: ${theme.colors.text.primary};
`;
